import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { translationService } from './FirebaseTranslationService';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
    const { i18n } = useTranslation();
    const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [translatedContent, setTranslatedContent] = useState(null);

    useEffect(() => {
        console.log('i18n language changed to:', i18n.language);
        setCurrentLanguage(i18n.language);
    }, [i18n.language]);

    const translateContent = useCallback(async (content) => {
        if (!content || currentLanguage === 'en') {
            if(!content) {console.log("No content to translate!");}
            setTranslatedContent(null);
            return;
        }

        setLoading(true);
        try {

            const translated = await translationService.translateMELContent(
                content,
                currentLanguage
            );

            // Only update if we have a valid MEL structure
            if (translated && translated.nodes && Array.isArray(translated.nodes)) {
                setTranslatedContent(translated);
            } else {
                console.warn('Received invalid translation structure:', translated);
            }
        } catch (err) {
            console.error('Translation error:', err);
            setError(err);
            setTranslatedContent(null);
        } finally {
            setLoading(false);
        }
    }, [currentLanguage]);


    const getNodeTranslation = useCallback((node) => {
        if (!translatedContent?.nodes || currentLanguage === 'en')
        {
            return null;
        }

        const translatedNode = translatedContent.nodes.find(n => n.id === node.id);
        return translatedNode?.display;
    }, [translatedContent, currentLanguage]);

    const changeLanguage = async (language) => {
        if (loading) return;

        setLoading(true);
        setError(null);

        try {
            await i18n.changeLanguage(language);
            setCurrentLanguage(language);
            localStorage.setItem('preferredLanguage', language);

            // Clear translations when switching back to English
            if (language === 'en') {
                setTranslatedContent(null);
            }
        } catch (error) {
            console.error('Error changing language:', error);
            setError(error.message || 'Failed to change language');
        } finally {
            setLoading(false);
        }
    };

    return (
        <LanguageContext.Provider value={{
            currentLanguage,
            changeLanguage,
            loading,
            error,
            translateContent,
            getNodeTranslation
        }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};